<template>
  <div class="chat-detail-page">
    <PageHeader :showTitle="false">
      <div class="flex items-center justify-between gap-3 text-white">
        <div class="flex items-center gap-3 min-w-0">
          <button
            type="button"
            class="inline-flex items-center rounded-md bg-primary-600 px-2 py-1 text-sm hover:bg-primary-700"
            @click="goBack"
          >
            <b-icon icon="fas fa-arrow-left" size="is-small" />
          </button>
          <div class="min-w-0">
            <p class="truncate text-lg font-semibold">
              {{ chatTitle }}
            </p>
            <p class="truncate text-xs text-gray-100">
              {{ chatSubtitle }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="inline-flex items-center rounded-md bg-primary-600 px-2 py-1 text-sm hover:bg-primary-700"
          @click="loadChat({ preserveScroll: true })"
        >
          <b-icon icon="fas fa-sync-alt" size="is-small" />
        </button>
      </div>
    </PageHeader>

    <PageContent>

      <section class="chat-panel chat-box rounded-lg border border-gray-200 bg-white">
        <div
          ref="messagesContainer"
          class="chat-messages-container"
        >
          <div v-if="loading" class="p-6 text-center text-gray-500">
            Chargement de la conversation...
          </div>

          <div v-else-if="error" class="p-6">
            <b-message type="is-danger" has-icon>
              {{ error }}
            </b-message>
          </div>

          <div v-else-if="messages.length === 0" class="p-8 text-center text-gray-500">
            Aucun message dans cette conversation.
          </div>

          <div v-else class="space-y-1 p-4">
            <div
              v-for="(messageItem, index) in messages"
              :key="messageItem.id"
            >
              <div v-if="shouldShowDayChip(messages, index)" class="my-3 flex justify-center">
                <span class="rounded-full bg-primary-700 px-3 py-1.5 text-[13px] leading-none text-white shadow-sm">
                  {{ dayChipLabel(messageItem.created_at) }}
                </span>
              </div>

              <div
                v-if="messageItem.is_system"
                class="mx-auto max-w-xl rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900"
              >
                {{ messageItem.content }}
              </div>

              <div
                v-else
                class="mt-2.5 flex px-2.5"
                :class="isMine(messageItem) ? 'justify-end' : 'justify-start'"
              >
                <div
                  class="chat-message-group"
                  :class="isMine(messageItem) ? 'chat-message-group--mine' : 'chat-message-group--other'"
                >
                  <div
                    class="chat-bubble relative"
                    :class="isMine(messageItem) ? 'chat-bubble--mine' : 'chat-bubble--other'"
                  >
                    <div class="chat-bubble__content">
                      <p
                        v-if="messageItem.source_title"
                        class="mb-1.5 text-[12px] font-semibold opacity-85"
                      >
                        Depuis l'actualité: {{ messageItem.source_title }}
                      </p>

                      <p
                        v-if="messageItem.content"
                        class="whitespace-pre-line break-words text-[16px] leading-tight"
                      >
                        {{ messageItem.content }}
                      </p>

                      <div v-if="!messageItem.source_title && !messageItem.content" class="h-4"></div>
                    </div>

                    <div
                      class="chat-bubble__meta"
                      :class="isMine(messageItem) ? 'chat-bubble__meta--mine' : 'chat-bubble__meta--other'"
                    >
                      <span>{{ formatTime(messageItem.created_at) }}</span>
                      <span v-if="isMine(messageItem)" class="chat-bubble__status">
                        {{ messageDeliveryStatus(messageItem) === 'read' ? '✓✓' : '✓' }}
                      </span>
                    </div>
                  </div>

                  <button
                    v-if="messageItem.attachment"
                    type="button"
                    class="chat-file-bubble mt-1 inline-flex min-w-0 items-center gap-2"
                    :class="isMine(messageItem) ? 'chat-file-bubble--mine' : 'chat-file-bubble--other'"
                    @click="downloadAttachment(messageItem)"
                  >
                    <b-icon icon="fas fa-paperclip" size="is-small" />
                    <span class="chat-file-name truncate">{{ attachmentName(messageItem) }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-composer border-t border-gray-200">
          <div v-if="!canWrite" class="mb-3 rounded bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
            Cette conversation est en lecture seule.
          </div>

          <div class="chat-composer__row">
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              @change="onFileChange"
            />

            <button
              type="button"
              class="chat-composer__icon-btn"
              @click="openFilePicker"
              :disabled="sending || !canWrite"
              title="Ajouter une pièce jointe"
            >
              <b-icon icon="fas fa-paperclip" size="is-small" />
            </button>

            <b-input
              class="chat-composer__field"
              custom-class="chat-composer__textarea"
              v-model="message"
              type="textarea"
              rows="2"
              expanded
              placeholder="Votre message..."
              :disabled="sending || !canWrite"
            />

            <button
              type="button"
              class="chat-composer__send-btn"
              @click="send"
              :disabled="!canSend"
              :class="{ 'is-loading': sending }"
              title="Envoyer"
            >
              <b-icon icon="fas fa-paper-plane" size="is-small" />
            </button>
          </div>

          <div
            v-if="file"
            class="chat-composer__file mt-3 flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
          >
            <div class="flex min-w-0 items-center gap-2 pr-3">
              <b-icon icon="fas fa-paperclip" size="is-small" />
              <span class="truncate">{{ file.name }}</span>
            </div>
            <button
              type="button"
              class="chat-composer__remove-btn"
              @click="removeFile"
            >
              Retirer
            </button>
          </div>
        </div>
      </section>
    </PageContent>
  </div>
</template>

<script>
import { parseISO, format } from 'date-fns'
import fr from 'date-fns/locale/fr'
import { mapGetters } from 'vuex'
import { http } from '@/services/api.service'
import PageHeader from '@/components/PageHeader.vue'
import PageContent from '@/components/PageContent.vue'
import { resolveContactDenomination } from '@/utils/contactDenomination'

export default {
  name: 'ChatDetailPage',
  components: {
    PageHeader,
    PageContent
  },
  data () {
    return {
      chat: null,
      loading: true,
      error: null,
      message: '',
      file: null,
      sending: false,
      refreshInterval: null,
      routeUuid: this.$route.params.uuid
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'application',
      'domain'
    ]),
    isTractApplication () {
      return String(this.application?.app?.slug || this.application?.slug || '').toLowerCase().trim() === 'tract'
    },
    cseLabel () {
      return resolveContactDenomination(this.domain, this.application)
    },
    applicationDisplayLabel () {
      return this.isTractApplication ? 'Tract' : 'Maviepro'
    },
    supportServiceLabel () {
      return `Service technique ${this.applicationDisplayLabel}`
    },
    messages () {
      if (!this.chat || !Array.isArray(this.chat.messages)) {
        return []
      }
      return this.chat.messages
    },
    counterpart () {
      if (!this.chat) {
        return null
      }

      if (this.chat.counterpart) {
        return this.chat.counterpart
      }

      if (!this.chat.initiator || !this.chat.assignee || !this.user) {
        return null
      }

      return this.chat.assignee.uuid === this.user.uuid ? this.chat.initiator : this.chat.assignee
    },
    chatTitle () {
      if (!this.counterpart) {
        return 'Conversation'
      }

      if (this.counterpart.is_service && this.counterpart.type === 'cse') {
        return this.cseLabel
      }

      if (this.counterpart.is_service && this.counterpart.type === 'maviepro') {
        return this.supportServiceLabel
      }

      if (this.counterpart.label) {
        return this.counterpart.label
      }

      const fullName = `${this.counterpart.firstname || ''} ${this.counterpart.lastname || ''}`.trim()
      if (fullName) {
        return fullName
      }

      return this.counterpart.email || 'Conversation'
    },
    chatSubtitle () {
      if (!this.counterpart) {
        return ''
      }

      if (this.counterpart.organization) {
        return this.counterpart.organization
      }

      if (this.counterpart.is_service && this.counterpart.type === 'maviepro') {
        return `Support ${this.applicationDisplayLabel}`
      }

      return this.counterpart.email || ''
    },
    canWrite () {
      if (!this.chat || !this.user) {
        return false
      }

      const recipientType = this.chat.recipient_type || 'interlocutor'
      if (recipientType === 'interlocutor') {
        return true
      }

      if (!this.chat.initiator || !this.chat.initiator.uuid) {
        return false
      }

      return this.chat.initiator.uuid === this.user.uuid
    },
    canSend () {
      const hasText = (this.message || '').trim().length > 0
      const hasFile = !!this.file
      return this.canWrite && !this.sending && (hasText || hasFile)
    }
  },
  mounted () {
    this.loadChat({ showLoader: true, forceScrollBottom: true })
    this.refreshInterval = window.setInterval(() => {
      this.loadChat({ preserveScroll: true })
    }, 12000)
  },
  beforeDestroy () {
    if (this.refreshInterval) {
      window.clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
  },
  beforeRouteUpdate (to, from, next) {
    this.routeUuid = to.params.uuid
    this.chat = null
    this.error = null
    this.message = ''
    this.file = null
    this.loadChat({ showLoader: true, forceScrollBottom: true })
    next()
  },
  methods: {
    goBack () {
      this.$router.push('/app/chats')
    },
    loadChat ({ showLoader = false, preserveScroll = false, forceScrollBottom = false } = {}) {
      if (!this.routeUuid) {
        return Promise.resolve()
      }

      const container = this.$refs.messagesContainer
      const shouldStickBottom = forceScrollBottom || !preserveScroll || this.isNearBottom(container)

      if (showLoader) {
        this.loading = true
      }

      return http.get(`/chats/${this.routeUuid}`).then(({ data }) => {
        this.chat = data
        this.error = null
        this.$store.dispatch('FETCH_MY_CHATS')
        this.$nextTick(() => {
          if (shouldStickBottom) {
            this.scrollToBottom()
          }
        })
      }).catch((err) => {
        if (err && err.response && [403, 404].includes(err.response.status)) {
          this.$router.push('/app/chats')
          return
        }

        this.error = (err && err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : 'Impossible de charger la conversation.'
      }).finally(() => {
        this.loading = false
      })
    },
    send () {
      if (!this.canSend || !this.chat) {
        return
      }

      this.sending = true
      const content = (this.message || '').trim()
      const formData = new FormData()

      if (content) {
        formData.append('content', content)
      }
      if (this.file) {
        formData.append('attachment', this.file)
      }

      const headers = { Enctype: 'multipart/form-data' }
      http.post(`/chats/${this.chat.uuid}/messages`, formData, headers).then(({ data }) => {
        if (!Array.isArray(this.chat.messages)) {
          this.$set(this.chat, 'messages', [])
        }
        this.chat.messages.push(data)
        this.message = ''
        this.file = null
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }).catch((err) => {
        const apiMessage = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Impossible d\'envoyer le message.'

        this.$buefy.notification.open({
          duration: 3500,
          message: apiMessage,
          position: 'is-bottom-left',
          type: 'is-danger',
          hasIcon: true
        })
      }).finally(() => {
        this.sending = false
      })
    },
    openFilePicker () {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    onFileChange (event) {
      const files = event && event.target && event.target.files ? event.target.files : []
      this.file = files && files.length > 0 ? files[0] : null
      if (event && event.target) {
        event.target.value = ''
      }
    },
    removeFile () {
      this.file = null
    },
    isMine (message) {
      if (!message || !message.creator || !this.user) {
        return false
      }
      return message.creator.uuid === this.user.uuid
    },
    authorName (message) {
      if (!message || !message.creator) {
        return 'Utilisateur'
      }
      const firstname = message.creator.firstname || ''
      const lastname = message.creator.lastname || ''
      const fullName = `${firstname} ${lastname}`.trim()
      return fullName || message.creator.email || 'Utilisateur'
    },
    attachmentName (message) {
      if (!message || !message.attachment) {
        return 'Pièce jointe'
      }

      if (typeof message.attachment === 'string') {
        return message.attachment
      }

      return message.attachment.name || 'Pièce jointe'
    },
    formatTime (date) {
      return this.formatDate(date, 'HH:mm')
    },
    messageDeliveryStatus (message) {
      if (!this.isMine(message)) {
        return null
      }

      if ((this.chat?.recipient_type || 'interlocutor') !== 'interlocutor') {
        return 'sent'
      }

      const messageDate = this.parseDate(message?.created_at)
      const counterpartReadDate = this.parseDate(this.chat?.counterpart_last_read_at)

      if (!messageDate || !counterpartReadDate) {
        return 'sent'
      }

      return counterpartReadDate.getTime() >= messageDate.getTime() ? 'read' : 'sent'
    },
    shouldShowDayChip (messages, index) {
      if (!Array.isArray(messages) || index < 0 || index >= messages.length) {
        return false
      }

      if (index === 0) {
        return true
      }

      return this.dayKey(messages[index]?.created_at) !== this.dayKey(messages[index - 1]?.created_at)
    },
    dayChipLabel (date) {
      const parsed = this.parseDate(date)
      if (!parsed) {
        return ''
      }

      const currentYear = new Date().getFullYear()
      const isPreviousYear = parsed.getFullYear() === currentYear - 1
      const options = isPreviousYear
        ? { day: 'numeric', month: 'long', year: 'numeric' }
        : { day: 'numeric', month: 'long' }

      return new Intl.DateTimeFormat('fr-FR', options).format(parsed)
    },
    downloadAttachment (message) {
      if (!this.chat || !this.chat.uuid || !message || !message.id) {
        return
      }

      http.get(`/request/${this.chat.uuid}/message/${message.id}/attachment`, {
        responseType: 'blob'
      }).then(({ data }) => {
        const fileURL = window.URL.createObjectURL(data)
        const fileName = this.attachmentName(message)
        const fileLink = document.createElement('a')
        fileLink.href = fileURL
        fileLink.setAttribute('download', fileName)

        document.body.appendChild(fileLink)
        fileLink.click()
        document.body.removeChild(fileLink)
        window.URL.revokeObjectURL(fileURL)
      }).catch(() => {
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Impossible de télécharger ce fichier.',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true
        })
      })
    },
    formatDate (date, dateFormat) {
      if (!date) {
        return ''
      }

      const normalized = String(date)
        .trim()
        .replace(/\s+/g, 'T')
        .replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})?$)/, '.$1')

      const parsed = parseISO(normalized)
      if (Number.isNaN(parsed.getTime())) {
        return ''
      }

      return format(parsed, dateFormat, { locale: fr })
    },
    dayKey (date) {
      const parsed = this.parseDate(date)
      if (!parsed) {
        return ''
      }

      return `${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`
    },
    parseDate (date) {
      if (!date) {
        return null
      }

      const normalized = String(date)
        .trim()
        .replace(/\s+/g, 'T')
        .replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})?$)/, '.$1')

      const parsed = new Date(normalized)

      if (Number.isNaN(parsed.getTime())) {
        return null
      }

      return parsed
    },
    isNearBottom (container) {
      if (!container) {
        return true
      }

      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight
      return distanceToBottom <= 120
    },
    scrollToBottom () {
      const container = this.$refs.messagesContainer
      if (!container) {
        return
      }
      container.scrollTop = container.scrollHeight
    }
  }
}
</script>

<style scoped lang="scss">
.chat-box {
  height: calc(100vh - 11rem);
  display: flex;
  flex-direction: column;
}

.chat-panel {
  max-width: 1080px;
  margin: 0 auto;
}

.chat-messages-container {
  flex: 1;
  overflow-y: auto;
  background-color: #f3f4f7;
}

.chat-composer {
  background: #fff;
  padding: 0.9rem;
}

.chat-message-group {
  display: flex;
  flex-direction: column;
  width: fit-content;
  max-width: 82%;
}

.chat-message-group--mine {
  align-items: flex-end;
}

.chat-message-group--other {
  align-items: flex-start;
}

.chat-bubble {
  position: relative;
  display: inline-block;
  width: auto;
  max-width: 100%;
  border-radius: 1rem;
  padding: 0.625rem 0.875rem 1rem;
  box-shadow: 0 1px 2px rgba(14, 22, 36, 0.12);
}

.chat-bubble--mine {
  background: #dbe3fe;
  color: #0b1e3d;
  border-bottom-right-radius: 10px;
}

.chat-bubble--other {
  background: #fff;
  color: #0b1e3d;
  border: 1px solid #dce1ea;
  border-bottom-left-radius: 10px;
}

.chat-bubble__content {
  padding-right: 3.75rem;
}

.chat-bubble__meta {
  position: absolute;
  right: 0.75rem;
  bottom: 0.55rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}

.chat-bubble__meta--mine {
  color: rgba(11, 30, 61, 0.72);
}

.chat-bubble__meta--other {
  color: #5f6e86;
}

.chat-bubble__status {
  letter-spacing: -1px;
}

.chat-file-bubble {
  max-width: 100%;
  border-radius: 1rem;
  padding: 0.625rem 0.875rem;
  box-shadow: 0 1px 2px rgba(14, 22, 36, 0.12);
}

.chat-file-bubble--mine {
  background: #dbe3fe;
  color: #1b3658;
  border-bottom-right-radius: 10px;
}

.chat-file-bubble--other {
  background: #fff;
  border: 1px solid #dce1ea;
  color: #1b3658;
  border-bottom-left-radius: 10px;
}

.chat-file-name {
  display: block;
  max-width: 65vw;
}

@media (min-width: 768px) {
  .chat-file-name {
    max-width: 26rem;
  }
}

.chat-composer__row {
  display: flex;
  align-items: flex-end;
  gap: 0.65rem;
}

.chat-composer__field {
  margin: 0;
  flex: 1;
}

.chat-composer__icon-btn,
.chat-composer__send-btn {
  border: 1px solid #d7dbe3;
  height: 2.5rem;
  width: 2.5rem;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.chat-composer__icon-btn {
  background: #f8fafc;
  color: #4b5563;
}

.chat-composer__icon-btn:hover {
  background: #eef2f7;
}

.chat-composer__send-btn {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.chat-composer__send-btn:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.chat-composer__send-btn:disabled,
.chat-composer__icon-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.chat-composer__remove-btn {
  color: #dc2626;
  font-weight: 600;
}

.chat-composer__remove-btn:hover {
  color: #b91c1c;
}

.chat-composer__file {
  line-height: 1.2;
}

::v-deep .chat-composer__field .control,
::v-deep .chat-composer__field .textarea {
  height: 100%;
}

::v-deep .chat-composer__textarea {
  border-radius: 14px;
  border: 1px solid #d7dbe3;
  min-height: 2.5rem;
  max-height: 7.5rem;
  resize: vertical;
  box-shadow: none;
  padding: 0.6rem 0.75rem;
  line-height: 1.25;
}

::v-deep .chat-composer__textarea:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 1px #60a5fa;
}
</style>
